/**
 * Service de tracking CTA pour New School
 * 
 * Envoie des événements vers Google Analytics (gtag) et stocke en localStorage pour analyse.
 * Compatible avec les campagnes Meta/Google Ads via UTM parameters.
 * 
 * Usage: import { trackCTA } from '../services/trackingService';
 *        trackCTA('hero_en_savoir_plus', 'Hero', 'scroll_to_catalogues');
 */

// Récupérer les paramètres UTM de l'URL courante (campagnes Meta, Google Ads, etc.)
const getUTMParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
        utm_source: params.get('utm_source') || '',
        utm_medium: params.get('utm_medium') || '',
        utm_campaign: params.get('utm_campaign') || '',
        utm_content: params.get('utm_content') || '',
        utm_term: params.get('utm_term') || ''
    };
};

// Stocker les UTM params en sessionStorage au premier chargement (persistance pendant la session)
const storeUTMParams = () => {
    const utmParams = getUTMParams();
    const hasUTM = Object.values(utmParams).some(v => v !== '');
    if (hasUTM) {
        sessionStorage.setItem('ns_utm', JSON.stringify(utmParams));
    }
};

// Récupérer les UTM stockés (ou ceux de l'URL courante)
const getStoredUTM = () => {
    try {
        const stored = sessionStorage.getItem('ns_utm');
        if (stored) return JSON.parse(stored);
    } catch (_) { /* ignore */ }
    return getUTMParams();
};

// Initialiser au premier import
storeUTMParams();

/**
 * Tracker un clic CTA
 * @param {string} ctaId    - Identifiant unique du CTA (ex: 'hero_devis_en_ligne')
 * @param {string} section  - Section de la page (ex: 'Header', 'Hero', 'Footer')
 * @param {string} action   - Action effectuée (ex: 'scroll_to_contact', 'open_instagram', 'submit_form')
 * @param {object} extra    - Données supplémentaires optionnelles
 */
export const trackCTA = (ctaId, section, action, extra = {}) => {
    const utm = getStoredUTM();
    const eventData = {
        event: 'cta_click',
        cta_id: ctaId,
        cta_section: section,
        cta_action: action,
        page_url: window.location.href,
        timestamp: new Date().toISOString(),
        ...utm,
        ...extra
    };

    // 1. Google Analytics 4 (gtag)
    if (typeof window.gtag === 'function') {
        window.gtag('event', 'cta_click', {
            event_category: section,
            event_label: ctaId,
            cta_action: action,
            ...utm,
            ...extra
        });
    }

    // 2. Meta Pixel (fbq)
    if (typeof window.fbq === 'function') {
        window.fbq('trackCustom', 'CTAClick', {
            cta_id: ctaId,
            section: section,
            action: action,
            ...utm
        });
    }

    // 3. dataLayer pour GTM (Google Tag Manager)
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(eventData);

    // 4. Stockage local pour analyse (dernier 200 événements max)
    try {
        const key = 'ns_cta_events';
        const events = JSON.parse(localStorage.getItem(key) || '[]');
        events.push(eventData);
        // Garder seulement les 200 derniers événements
        if (events.length > 200) events.splice(0, events.length - 200);
        localStorage.setItem(key, JSON.stringify(events));
    } catch (_) { /* quota exceeded - ignore */ }

    // 5. Console en dev
    if (process.env.NODE_ENV === 'development') {
        console.log(`📊 [CTA Track] ${section} > ${ctaId} > ${action}`, eventData);
    }
};

/**
 * Tracker une soumission de formulaire
 */
export const trackFormSubmit = (formId, section, extra = {}) => {
    trackCTA(formId, section, 'form_submit', { conversion: true, ...extra });

    // Conversion GA4
    if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
            event_category: 'form',
            event_label: formId,
            ...extra
        });
    }

    // Conversion Meta Pixel
    if (typeof window.fbq === 'function') {
        window.fbq('track', 'Lead', {
            content_name: formId,
            ...extra
        });
    }
};

/**
 * Tracker un clic sortant (Instagram, Facebook, TikTok, etc.)
 */
export const trackOutboundClick = (ctaId, section, url) => {
    trackCTA(ctaId, section, 'outbound_click', { outbound_url: url });
};

/**
 * Tracker l'ouverture/fermeture du menu burger
 */
export const trackMenuToggle = (isOpening) => {
    trackCTA(
        'burger_menu',
        'Header',
        isOpening ? 'menu_open' : 'menu_close'
    );
};

/**
 * Tracker un téléchargement de PDF
 */
export const trackPDFDownload = (pdfName) => {
    trackCTA(`pdf_${pdfName.toLowerCase().replace(/\s+/g, '_')}`, 'Catalogues', 'pdf_download', {
        file_name: pdfName
    });
};

/**
 * Tracker un clic FAQ (ouverture de question)
 */
export const trackFAQToggle = (questionIndex, questionText) => {
    trackCTA(`faq_question_${questionIndex}`, 'FAQ', 'faq_open', {
        question: questionText.substring(0, 100)
    });
};

/**
 * Récupérer tous les événements stockés (pour debug/export)
 */
export const getStoredEvents = () => {
    try {
        return JSON.parse(localStorage.getItem('ns_cta_events') || '[]');
    } catch (_) {
        return [];
    }
};

/**
 * Vider les événements stockés
 */
export const clearStoredEvents = () => {
    localStorage.removeItem('ns_cta_events');
};
